import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, User } from '../../services/auth.service';
import { UsersService } from '../../services/users.service';
import { ConfirmationService } from '../../shared/services/confirmation.service';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css']
})
export class UserProfileComponent implements OnInit {
  @ViewChild('imageCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  
  currentUser: User | null = null;
  userName: string = '';
  userEmail: string = '';
  userProfile: any = null;
  loading: boolean = false;
  
  // Avatar properties
  avatarUrl: string = '';
  showCropper: boolean = false;
  selectedImage: HTMLImageElement | null = null;
  croppedImageBase64: string = '';
  uploadingAvatar: boolean = false;
  
  // Crop properties
  private cropStartX = 0;
  private cropStartY = 0;
  private cropSize = 0;
  private scale = 1;
  private imageX = 0;
  private imageY = 0;
  private isDragging = false;
  private lastX = 0;
  private lastY = 0;

  constructor(
    private authService: AuthService,
    private router: Router,
    private usersService: UsersService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadUserData();
  }

  loadUserData(): void {
    this.currentUser = this.authService.currentUserValue;
    // Obter userId do token JWT
    const userId = this.getUserIdFromToken();
    if (userId) {
      this.loading = true;
      this.usersService.getUserById(userId).subscribe({
        next: (profile) => {
          this.userProfile = profile;
          this.userName = profile.Name || '';
          this.userEmail = profile.Email || '';
          // Se StaffId existir e Staff não estiver preenchido, buscar Staff completo
          if (profile.StaffId && !profile.Staff) {
            // Buscar dados completos do staff
            this.usersService.staffService?.getStaffById(profile.StaffId).subscribe({
              next: (staff) => {
                this.userProfile.Staff = this.normalizeStaffFields(staff);
                this.loading = false;
                this.loadAvatar(userId);
              },
              error: (err) => {
                this.loading = false;
                this.loadAvatar(userId);
              }
            });
          } else {
            // Normaliza campos do staff se já vierem no profile
            if (profile.Staff) {
              this.userProfile.Staff = this.normalizeStaffFields(profile.Staff);
            }
            this.loading = false;
            this.loadAvatar(userId);
          }
        },
        error: (error) => {
          console.error('Erro ao carregar perfil do usuário:', error);
          this.loading = false;
          // Fallback para dados do token
          this.loadDataFromToken();
        }
      });
    } else {
      // Fallback para dados do token
      this.loadDataFromToken();
    }
  }

  /**
   * Normaliza os campos de Departamento, Posição e Gerente do Staff,
   * usando DepartmentName/PositionName caso Department/Position estejam nulos.
   */
  private normalizeStaffFields(staff: any): any {
    if (!staff) return staff;
    // Departamento
    if (!staff.Department && staff.DepartmentName) {
      staff.Department = staff.DepartmentName;
    }
    // Posição
    if (!staff.Position && staff.PositionName) {
      staff.Position = staff.PositionName;
    }
    // Gerente: tenta Manager, ManagerName, ou deixa nulo
    if (!staff.Manager && staff.ManagerName) {
      staff.Manager = staff.ManagerName;
    }
    return staff;
  }

  private getUserIdFromToken(): number | null {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) {
      return null;
    }

    const parsedUserId = Number(userId);
    return Number.isFinite(parsedUserId) && parsedUserId > 0 ? parsedUserId : null;
  }

  private loadDataFromToken(): void {
    if (this.currentUser) {
      this.userName = this.currentUser.username || '';
      this.userEmail = this.currentUser.email || '';
    } else {
      this.userName = this.authService.getUserNameFromToken();
      const token = this.authService.getToken();
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          this.userEmail = payload.email || payload.unique_name || '';
        } catch (error) {
          console.error('Erro ao extrair email do token:', error);
        }
      }
    }
  }

  logout(): void {
    this.confirmationService
      .confirm({
        title: 'Confirmar saída',
        message: 'Tem certeza que deseja sair?',
        confirmText: 'Sair',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        type: 'danger'
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.authService.logout();
        this.router.navigate(['/login']);
      });
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  // Avatar methods
  loadAvatar(userId: number): void {
    this.usersService.getAvatar(userId).subscribe({
      next: (response) => {
        if (response && response.avatar) {
          // Verifica se o base64 já tem o prefixo data:image
          if (response.avatar.startsWith('data:image')) {
            this.avatarUrl = response.avatar;
          } else {
            // Adiciona o prefixo para imagens PNG por padrão
            this.avatarUrl = `data:image/png;base64,${response.avatar}`;
          }
        }
      },
      error: (error) => {
        // Usa imagem padrão
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          this.selectedImage = img;
          this.showCropper = true;
          setTimeout(() => this.initCropper(), 100);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      alert('Por favor, selecione uma imagem válida');
    }
  }

  initCropper(): void {
    if (!this.selectedImage || !this.canvasRef) return;
    
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = 400;
    canvas.height = 400;

    // Calculate initial scale and position
    const minDim = Math.min(this.selectedImage.width, this.selectedImage.height);
    this.scale = canvas.width / minDim;
    this.imageX = (canvas.width - this.selectedImage.width * this.scale) / 2;
    this.imageY = (canvas.height - this.selectedImage.height * this.scale) / 2;

    this.drawCanvas();
  }

  drawCanvas(): void {
    if (!this.selectedImage || !this.canvasRef) return;
    
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    ctx.save();
    ctx.drawImage(
      this.selectedImage,
      this.imageX,
      this.imageY,
      this.selectedImage.width * this.scale,
      this.selectedImage.height * this.scale
    );
    ctx.restore();

    // Draw crop circle overlay
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width / 2 - 20;

    // Darken outside the circle
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();
  }

  onCanvasMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.lastX = event.offsetX;
    this.lastY = event.offsetY;
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    
    const dx = event.offsetX - this.lastX;
    const dy = event.offsetY - this.lastY;
    
    this.imageX += dx;
    this.imageY += dy;
    
    this.lastX = event.offsetX;
    this.lastY = event.offsetY;
    
    this.drawCanvas();
  }

  onCanvasMouseUp(): void {
    this.isDragging = false;
  }

  onCanvasWheel(event: WheelEvent): void {
    event.preventDefault();
    
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    this.scale *= delta;
    
    // Limit scale
    this.scale = Math.max(0.1, Math.min(5, this.scale));
    
    this.drawCanvas();
  }

  cancelCrop(): void {
    this.showCropper = false;
    this.selectedImage = null;
    this.croppedImageBase64 = '';
    this.scale = 1;
    this.imageX = 0;
    this.imageY = 0;
    // Limpar o input file
    const fileInput = document.getElementById('avatarInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  async saveCroppedImage(): Promise<void> {
    if (!this.selectedImage || !this.canvasRef) {
      alert('Erro ao processar imagem');
      return;
    }

    const userId = this.getUserIdFromToken();
    if (!userId) {
      alert('Erro ao identificar usuário');
      return;
    }

    this.uploadingAvatar = true;

    const canvas = this.canvasRef.nativeElement;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width / 2 - 20;

    // Criar um canvas intermediário para desenhar apenas a imagem sem overlay
    const intermediateCanvas = document.createElement('canvas');
    intermediateCanvas.width = canvas.width;
    intermediateCanvas.height = canvas.height;
    const intermediateCtx = intermediateCanvas.getContext('2d');
    
    if (!intermediateCtx) {
      alert('Erro ao criar imagem');
      this.uploadingAvatar = false;
      return;
    }

    // Desenhar apenas a imagem original sem o overlay
    intermediateCtx.drawImage(
      this.selectedImage,
      this.imageX,
      this.imageY,
      this.selectedImage.width * this.scale,
      this.selectedImage.height * this.scale
    );

    // Criar canvas final para a imagem cortada circular
    const tempCanvas = document.createElement('canvas');
    const size = 300;
    tempCanvas.width = size;
    tempCanvas.height = size;
    const tempCtx = tempCanvas.getContext('2d');
    
    if (!tempCtx) {
      alert('Erro ao criar imagem');
      this.uploadingAvatar = false;
      return;
    }

    // Criar máscara circular e aplicar o corte
    tempCtx.save();
    tempCtx.beginPath();
    tempCtx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    tempCtx.clip();
    
    // Calcular área a ser extraída do canvas intermediário
    const sourceX = centerX - radius;
    const sourceY = centerY - radius;
    const sourceSize = radius * 2;
    
    // Desenhar a área cortada do canvas intermediário (sem overlay)
    tempCtx.drawImage(
      intermediateCanvas,
      sourceX, sourceY, sourceSize, sourceSize,
      0, 0, size, size
    );
    tempCtx.restore();

    this.croppedImageBase64 = tempCanvas.toDataURL('image/png');
    
    // Envia o base64 completo com o prefixo data:image/png;base64,
    const base64Data = this.croppedImageBase64;

    const uploadObservable = this.avatarUrl 
      ? this.usersService.updateAvatar(userId, base64Data, userId.toString())
      : this.usersService.uploadAvatar(userId, base64Data, userId.toString());

    uploadObservable.subscribe({
      next: (response) => {
        this.avatarUrl = this.croppedImageBase64;
        this.cancelCrop();
        this.uploadingAvatar = false;
        alert('Foto de perfil atualizada com sucesso!');
      },
      error: (error) => {
        console.error('Erro ao salvar avatar:', error);
        alert('Erro ao salvar foto de perfil. Tente novamente.');
        this.uploadingAvatar = false;
      }
    });
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('avatarInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }
}
